/**
 * tool-embed.ts — "Embed on your website" affordance for public tool pages.
 *
 * Progressive enhancement on a server-rendered popover:
 *   [data-tn-embed-root]   — wrapper (position anchor for the popover)
 *   [data-tn-embed-toggle] — button that opens/closes the panel
 *   [data-tn-embed-panel]  — the popover holding the <textarea> snippet
 *   [data-tn-embed-copy]   — one-click copy button
 *
 * The snippet is fully rendered server-side, so with JS off the panel can still
 * be revealed and copied manually. This module only adds toggle + clipboard.
 */
import { devLog } from "./dev-utils.js";
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
let openRoot = null;
function closePanel() {
    if (openRoot === null)
        return;
    const toggle = openRoot.querySelector("[data-tn-embed-toggle]");
    const panel = openRoot.querySelector("[data-tn-embed-panel]");
    if (panel !== null)
        panel.hidden = true;
    if (toggle !== null)
        toggle.setAttribute("aria-expanded", "false");
    openRoot = null;
    document.removeEventListener("click", onDocumentClick, true);
    document.removeEventListener("keydown", onKeydown, true);
}
function onDocumentClick(ev) {
    if (openRoot !== null && !openRoot.contains(ev.target)) {
        closePanel();
    }
}
function onKeydown(ev) {
    if (ev.key === "Escape")
        closePanel();
}
function openPanel(root) {
    const toggle = root.querySelector("[data-tn-embed-toggle]");
    const panel = root.querySelector("[data-tn-embed-panel]");
    if (panel === null)
        return;
    panel.hidden = false;
    if (toggle !== null)
        toggle.setAttribute("aria-expanded", "true");
    openRoot = root;
    const code = panel.querySelector("textarea");
    code?.focus();
    code?.select();
    window.setTimeout(() => {
        document.addEventListener("click", onDocumentClick, true);
        document.addEventListener("keydown", onKeydown, true);
    }, 0);
}
async function copySnippet(root) {
    const code = root.querySelector("textarea");
    const btn = root.querySelector("[data-tn-embed-copy]");
    if (code === null)
        return;
    const writer = navigator.clipboard?.writeText;
    if (typeof writer === "function") {
        try {
            await writer.call(navigator.clipboard, code.value);
            flashCopied(btn);
            return;
        }
        catch {
            /* fall through to manual select */
        }
    }
    code.focus();
    code.select();
}
function flashCopied(btn) {
    if (btn === null)
        return;
    const original = btn.textContent;
    btn.textContent = t("js.toolEmbed.copied", "Copied!");
    window.setTimeout(() => {
        btn.textContent = original;
    }, 1600);
}
function bindRoot(root) {
    if (root.dataset["tnEmbedBound"] === "1")
        return;
    root.dataset["tnEmbedBound"] = "1";
    const toggle = root.querySelector("[data-tn-embed-toggle]");
    const copy = root.querySelector("[data-tn-embed-copy]");
    toggle?.addEventListener("click", (ev) => {
        ev.preventDefault();
        if (openRoot === root) {
            closePanel();
        }
        else {
            closePanel();
            openPanel(root);
        }
    });
    copy?.addEventListener("click", (ev) => {
        ev.preventDefault();
        void copySnippet(root);
    });
}
function init() {
    const roots = document.querySelectorAll("[data-tn-embed-root]");
    devLog("embed", "Found", roots.length, "embed control(s)");
    roots.forEach(bindRoot);
}
if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
    }
    else {
        init();
    }
}
//# sourceMappingURL=tool-embed.js.map